import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import lessonGIFtransparent from "../../assets/images/lessonGIFtransparent.gif";
import DOMPurify from "dompurify";
import api, { wsURL } from "../../api/api";
import { getUserId } from "../../api/get_user_id";
import { useAuth } from "../../features/auth/AuthContext";
import PrimaryBtn from "../Buttons/PrimaryBtn";
import LowerSchoolStoryStage from "../../pages/home/components/dashboard/teacher/lessons/NewLessons/LessonGenerator/LowerSchoolStoryStage";
import {
  LowerSchoolStoryMode,
  LowerSchoolStoryStatus,
  useLowerSchoolStorySocket,
} from "../../pages/home/components/dashboard/teacher/lessons/NewLessons/LessonWebSocket/useLowerSchoolStorySocket";
import {
  enterFullscreen,
  exitFullscreen,
  isFullscreen,
  isFullscreenSupported,
  onFullscreenChange,
} from "../../utils/fullscreen";
import "./LSLesson.css";
// import BackButton from "../BackButton/BackButton";
// import FloatingAIButton from "../FloatingAIButton/FloatingAIButton";
// import { useDispatch, useSelector } from "react-redux";
// import { setAgentData, setDrawerState, setInitialProjectId, setIsWebAllowed, setQueryMetaData } from "../../state/reducers/chatDrawerReducers";

/** Sits under the loader gif on every wait, from room setup to the next scene. */
const LOADER_CAPTION = "Your story is on its way";

/** How long to wait for the completion screen before showing the local end card. */
const COMPLETION_WAIT_MS = 30000;

/**
 * The parts of `completion_html` the completion clip reads out, and so the only
 * parts that get the read-along — the heading and the CTA are page furniture,
 * and anything the clip does not cover would only drift out of step with it.
 * Add a selector here if the narration grows to take in more of the card.
 */
const NARRATED_COMPLETION_SELECTORS = [".ls-lesson-description"];

/**
 * Student attempt at a Lower School lesson.
 *
 * Same socket and same story card as the teacher's Student Preview
 * (`StoryPreview`) — the only differences are the mode (`student`, so the
 * backend strips `is_correct` / `outcome` out of every option before it reaches
 * the browser) and the ending: a `story_end` frame here submits the attempt,
 * where the teacher's preview just closes.
 */
/**
 * Grade ids (`student_details.data.grade` is `{ id: label }`) whose students
 * get the narration read out automatically: Pre-K "1", Kindergarten "2",
 * Grade 1 "3". From Grade 2 ("4") up, it plays only off the speaker button.
 */
const AUTO_NARRATION_GRADE_IDS = ["1", "2", "3"];

const LSLesson = () => {
  const { course_id, lesson_id } = useParams();
  const { token } = useAuth();
  const studentGrade = useSelector(
    (state) => state?.globalState?.student_details?.data?.grade,
  );
  // Below Grade 2 the story reads itself out. An unknown grade keeps the
  // autoplay rather than leaving a young reader on a silent screen.
  const studentGradeId = Object.keys(studentGrade || {})[0];
  const autoPlayNarration =
    !studentGradeId || AUTO_NARRATION_GRADE_IDS.includes(String(studentGradeId).trim());
  const navigate = useNavigate();
  const location = useLocation();
  // const dispatch = useDispatch();
  const course = location?.state?.course || {};
  // const chatDrawerState = useSelector((state) => state?.ChatDrawerState);

  /** Where the student came from — falls back to the course page. */
  const backTo = location.state?.goBack || `/course/${course_id}`;

  /** Chat room the story session runs in; the socket stays closed until it lands. */
  const [roomId, setRoomId] = useState("");
  const [roomError, setRoomError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Set once `submit` has gone out — takes precedence over the socket status. */
  const [isSubmitted, setIsSubmitted] = useState(false);

  // The story session needs a lesson chat room, same as the teacher side. It is
  // created per user, so a re-attempt resumes the student's own session.
  useEffect(() => {
    if (!token || !lesson_id) return undefined;

    let isCancelled = false;

    const setupRoom = async () => {
      try {
        const userId = await getUserId(token);
        if (isCancelled) return;
        if (!userId) {
          setRoomError("Could not start the lesson. Please try again.");
          return;
        }

        const response = await api.post(
          `ai/api/lesson_create_chat_room/`,
          { user_id: userId, against_user_id: userId },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          },
        );
        if (isCancelled) return;

        const room = response?.data?.room_id;
        if (room) {
          setRoomId(room);
        } else {
          setRoomError("Could not start the lesson. Please try again.");
        }
      } catch (error) {
        // The student can navigate away mid-request — don't touch state then.
        if (isCancelled) return;
        console.error("Lower school lesson: room setup failed", error);
        setRoomError(
          error.response?.data?.message ||
            "Could not start the lesson. Please try again.",
        );
      }
    };

    setupRoom();

    return () => {
      isCancelled = true;
    };
  }, [token, lesson_id]);

  const {
    status,
    intro,
    node,
    generating,
    storyEnd,
    completed,
    feedback,
    socketError,
    startStory,
    answerQuestion,
    goBack,
    submitStory,
  } = useLowerSchoolStorySocket({
    wsURL,
    token,
    roomId,
    lessonId: lesson_id,
    mode: LowerSchoolStoryMode.STUDENT,
    enabled: Boolean(roomId),
  });

  /** A failed submit leaves the button live so the student can retry. */
  const [submitError, setSubmitError] = useState("");
  /** Latest socket error, so a rejected submit can surface on this card. */
  const socketErrorRef = useRef(socketError);
  useEffect(() => {
    socketErrorRef.current = socketError;
  }, [socketError]);

  /** Guards the auto-submit so `story_end` only ever sends one `submit`. */
  const hasSubmittedRef = useRef(false);

  const sendSubmit = useCallback(() => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    setSubmitError("");
    setIsSubmitting(true);
    if (submitStory()) {
      setIsSubmitted(true);
      // toast.success("Lesson submitted.", { position: "top-center" });
    } else {
      // sendAction only fails when the socket is down — it has already set the
      // socket error; repeat it here so it shows on the submit card, and free
      // the guard so the student can retry from the button.
      hasSubmittedRef.current = false;
      setSubmitError(
        socketErrorRef.current || "Please try again after sometime.",
      );
    }
    setIsSubmitting(false);
  }, [submitStory]);

  // `story_end` is the end of the story — the attempt submits itself right then,
  // so the student never has to. The button on the card is only the retry path
  // for when the socket was down at that moment.
  useEffect(() => {
    if (status !== LowerSchoolStoryStatus.ENDED) return;
    sendSubmit();
  }, [status, sendSubmit]);

  const handleBackToCourse = () => {
    exitFullscreen();
    navigate(backTo);
  };

  // Fullscreen itself starts on the lesson list's Start button (`LessonCard`),
  // the click that opens this page. Any way out of the lesson — the end screen's Back To Course, the stage's
  // Back buttons, browser back — unmounts this page, so fullscreen ends with it
  // instead of carrying over onto the course page.
  useEffect(() => () => exitFullscreen(), []);

  // The app header is rendered by App.js, outside this page — this class lets
  // LSLesson.css hide it while the story is fullscreen (and only then).
  useEffect(() => {
    document.body.classList.add("ls-lesson-active");
    return () => document.body.classList.remove("ls-lesson-active");
  }, []);

  /** Tracks Esc / browser exits so the Full Screen button can offer a way back. */
  const [isPageFullscreen, setIsPageFullscreen] = useState(isFullscreen);
  useEffect(
    () => onFullscreenChange(() => setIsPageFullscreen(isFullscreen())),
    [],
  );

  /**
   * Sits to the right of the top Back button once the student has left
   * fullscreen, so they can go back into it. Hidden while fullscreen, and where
   * the browser can't do fullscreen at all.
   */
  const fullscreenButton =
    !isPageFullscreen && isFullscreenSupported() ? (
      <button
        type="button"
        className="story-preview-back-to-course"
        onClick={enterFullscreen}
      >
        <i className="fa-solid fa-expand"></i> Full Screen
      </button>
    ) : null;

  /** Second line under the caption — whatever step the backend reports. */
  const loaderNote =
    typeof generating?.message === "string" ? generating.message : "";

  /**
   * The completion screen rides on the `feedback` frame, which lands after
   * `completed` once the backend has written the child's feedback into
   * `completion_html` and narrated it. Until then there is no markup, and the
   * loader (or the submitted card) stands in.
   *
   * Only the `feedback` frame is rendered — any markup or audio on the
   * `completed` frame is ignored, so the student never sees that screen.
   */
  const completionFrame = feedback;

  // completion_html is backend-authored markup — sanitize before injecting.
  const completionHtml = completionFrame?.completion_html
    ? DOMPurify.sanitize(completionFrame.completion_html)
    : "";

  /**
   * Set if the completion screen hasn't arrived within
   * COMPLETION_WAIT_MS of the submit, so the local end card can take over
   * instead of leaving the student on the loader indefinitely.
   */
  const [completionTimedOut, setCompletionTimedOut] = useState(false);
  const isAwaitingCompletion =
    (isSubmitted || Boolean(completed) || Boolean(feedback)) && !completionHtml;
  useEffect(() => {
    if (!isAwaitingCompletion) return undefined;
    setCompletionTimedOut(false);
    const timer = setTimeout(
      () => setCompletionTimedOut(true),
      COMPLETION_WAIT_MS,
    );
    return () => clearTimeout(timer);
  }, [isAwaitingCompletion]);

  /**
   * Between the submit and the `feedback` frame, keep the loader up rather
   * than flashing the local end card — the real screen is on its way. A
   * socket error or a timeout falls back to the end card.
   */
  const showCompletionLoader =
    isAwaitingCompletion && !socketError && !submitError && !completionTimedOut;

  /**
   * The clip that reads the completion screen out. Optional — the frame only
   * carries it once the backend has narrated that screen, so an attempt that
   * finishes without one simply stays silent.
   */
  const completionAudioUrl = completionFrame?.completion_audio_url || "";
  const completionAudioRef = useRef(null);
  /** The injected completion markup, so the read-along can be built inside it. */
  const completionRef = useRef(null);
  /** One span per narrated word, in reading order. */
  const completionWordsRef = useRef([]);
  /** Fraction of the clip each word is read by — see the story card's version. */
  const completionBoundariesRef = useRef([]);
  /** Last index marked spoken, so a tick only has to touch the difference. */
  const spokenUptoRef = useRef(-1);

  /**
   * The page sits straight under the app header, whose height changes with the
   * breakpoint. Measure where the page starts and publish it as `--ls-page-top`
   * so the CSS can fill exactly the rest of the viewport — no gap, no scroll.
   */
  const pageRef = useRef(null);
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const syncTop = () => {
      const top = page.getBoundingClientRect().top + window.scrollY;
      page.style.setProperty("--ls-page-top", `${Math.max(0, top)}px`);
    };

    syncTop();
    window.addEventListener("resize", syncTop);
    // The header can reflow without a window resize (e.g. fonts loading).
    const header = document.querySelector(".dashboardHeader");
    const observer =
      header && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncTop)
        : null;
    observer?.observe(header);

    return () => {
      window.removeEventListener("resize", syncTop);
      observer?.disconnect();
    };
  }, []);

  /**
   * Wraps every narrated word in its own span. `completion_html` arrives as
   * markup rather than React children, so unlike the story card — which maps
   * over `substory` and renders the spans itself — the read-along has to be
   * built in the DOM once the markup is on screen. React never reconciles
   * inside `dangerouslySetInnerHTML`, so these stay put until the html itself
   * changes, which is exactly when this runs again.
   */
  useEffect(() => {
    completionWordsRef.current = [];
    completionBoundariesRef.current = [];
    spokenUptoRef.current = -1;

    const root = completionRef.current;
    // No clip, no read-along: the words would go grey and never light up.
    if (!root || !completionHtml || !completionAudioUrl) return;

    const spans = [];
    NARRATED_COMPLETION_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        // Collect the text nodes before touching any of them — replacing one
        // mid-walk would feed the walker the spans it had just created.
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode);

        textNodes.forEach((textNode) => {
          // The separators are captured so the original spacing survives.
          const parts = textNode.nodeValue.split(/(\s+)/);
          if (!parts.some((part) => part.trim())) return;
          const fragment = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (!part.trim()) {
              fragment.appendChild(document.createTextNode(part));
              return;
            }
            const span = document.createElement("span");
            span.className = "ls-word";
            span.textContent = part;
            spans.push(span);
            fragment.appendChild(span);
          });
          textNode.parentNode?.replaceChild(fragment, textNode);
        });
      });
    });

    if (!spans.length) return;

    // No word timings come down with the clip, so each word gets a slice of it
    // proportional to its length — the same approximation the story narration
    // uses, and it self-corrects on every tick because the boundaries are
    // fractions of the real duration rather than a fixed cadence.
    const weights = spans.map((span) => span.textContent.length + 1);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let consumed = 0;
    completionBoundariesRef.current = weights.map((weight) => {
      consumed += weight;
      return consumed / totalWeight;
    });
    completionWordsRef.current = spans;
  }, [completionHtml, completionAudioUrl]);

  /** Lights up to `index`, and back off again when the clip is seeked back. */
  const markCompletionSpoken = useCallback((index) => {
    const spans = completionWordsRef.current;
    const from = spokenUptoRef.current;
    if (index === from) return;
    if (index > from) {
      for (let i = from + 1; i <= index; i += 1) {
        spans[i]?.classList.add("is-spoken");
      }
    } else {
      for (let i = from; i > index; i -= 1) {
        spans[i]?.classList.remove("is-spoken");
      }
    }
    spokenUptoRef.current = index;
  }, []);

  const handleCompletionTimeUpdate = () => {
    const el = completionAudioRef.current;
    const boundaries = completionBoundariesRef.current;
    if (!el || !el.duration || !boundaries.length) return;
    const played = el.currentTime / el.duration;
    const index = boundaries.findIndex((boundary) => played <= boundary);
    markCompletionSpoken(index === -1 ? boundaries.length - 1 : index);
  };

  /** Leave the whole message highlighted once the clip finishes. */
  const handleCompletionAudioEnded = () => {
    markCompletionSpoken(completionWordsRef.current.length - 1);
  };

  /** Starts the completion clip, unless it is already running. */
  const playCompletionAudio = useCallback(() => {
    const el = completionAudioRef.current;
    if (!el || !el.paused) return;
    const played = el.play();
    // A refused autoplay just rejects — `handleCompletionClick` gives it
    // another go off the student's next touch on the screen.
    if (played?.catch) played.catch(() => {});
  }, []);

  // Read the screen out as it appears. Having clicked through the whole story
  // the student has normally given the page the activation autoplay needs; the
  // one case that can be refused is a resume, where `feedback` lands straight
  // after connect and nothing has been touched yet.
  useEffect(() => {
    if (!completionAudioUrl) return;
    playCompletionAudio();
  }, [completionAudioUrl, playCompletionAudio]);

  // The element goes out with the page — pause it by hand on the way, since a
  // detached media element carries on playing in some browsers.
  useEffect(
    () => () => {
      completionAudioRef.current?.pause();
    },
    [],
  );

  /**
   * The completion markup ships its own "Back To Course" button, so we wire the
   * backend's rather than rendering one — same delegation the intro's start
   * button uses, since events bubble out of `dangerouslySetInnerHTML` content
   * like any other DOM.
   */
  const handleCompletionClick = (event) => {
    if (event.target.closest(".ls-back-btn")) {
      event.preventDefault();
      handleBackToCourse();
      return;
    }
    // Any other touch on the screen is a user gesture, which is all a browser
    // that refused the autoplay above was waiting for.
    playCompletionAudio();
  };

  // The end card. Submission has normally already gone out with `story_end`, so
  // this is the confirmation; the Retry button only appears when that send
  // failed because the socket was down. It gives way to `completion_html` as
  // soon as the `feedback` frame lands.
  const endSlot = (
    <div className="ls-lesson-end">
      <span className="ls-lesson-end-title">
        {isSubmitted ? "Lesson submitted!" : "You finished the story!"}
      </span>
      <span className="ls-lesson-end-text">
        {isSubmitted
          ? "Your teacher can see your answers now."
          : storyEnd?.message || "Sending your answers to your teacher..."}
      </span>
      {submitError && (
        <span className="ls-lesson-end-error">{submitError}</span>
      )}
      {isSubmitted ? (
        <PrimaryBtn label="Back To Course" handleClick={handleBackToCourse} />
      ) : (
        <PrimaryBtn
          label={isSubmitting ? "Submitting..." : "Retry Submit"}
          disabled={isSubmitting}
          handleClick={sendSubmit}
        />
      )}
    </div>
  );

  return (
    // the completion markup is designed against a cream page — it takes the
    // whole shell with it, back button included
    <>
    <div
      ref={pageRef}
      className={`ls-lesson-page relative`}
    >
        {/* <div
          className="Back_button"
          onClick={() => {
            if (location?.state?.goBack) {
              navigate(location.state.goBack, { state: "after submission" });
            } else {
              navigate(`/course/${course_id}/`, { state: "after submission" });
            }
          }}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: "#0068ff",
          }}
        >
          <i className="fa-solid fa-chevron-left"></i>
          <span style={{ padding: "0px 5px" }}>{location?.state?.lesson?.name}</span>
        </div> */}
      {roomError ? (
        <div className="ls-lesson-status ls-lesson-status-error">
          <span>{roomError}</span>
          <PrimaryBtn label="Back To Course" handleClick={handleBackToCourse} />
        </div>
      ) : !roomId ? (
        // The same loader the story itself uses, so the room setup and the wait
        // for the intro read as one continuous wait.
        <div className="story-preview-frameless">
          <div className="story-preview-content is-frameless">
            {/* Same Back as the stage's loader, so it is there from the first
                frame instead of appearing once the room is created. */}
            <div className={`flex justify-between items-center gap-4 w-full ${isPageFullscreen ? "mt-5" : ""}`}>
              <button
                type="button"
                className="story-preview-back-to-course"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-chevron-left"></i> Back
              </button>
              {fullscreenButton}
            </div>
            <div className="story-preview-loader">
              <img
                className="story-preview-loader-gif"
                src={lessonGIFtransparent}
                alt=""
              />
              {/* <span className="story-preview-loader-title">
                {LOADER_CAPTION}
              </span> */}
            </div>
          </div>
        </div>
      ) : // Either frame on its own covers the resume case — the backend sends
      // them straight after connect when this attempt was already submitted.
      completed || feedback || isSubmitted ? (
        // Past submission the end screen owns the page outright: the socket
        // stays open (the hook lives here, not in the stage) so the submit frame
        // is never cut off, but a reconnect can no longer put the story back on
        // screen behind the student.
        <div className="story-preview-frameless">
          <div className="story-preview-content is-frameless">
            {completionAudioUrl && (
              // Sits outside the branch below so it survives `completion_html`
              // arriving after the card, and carries no `controls` — the UA
              // stylesheet keeps it out of the layout either way.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                ref={completionAudioRef}
                src={completionAudioUrl}
                onTimeUpdate={handleCompletionTimeUpdate}
                onEnded={handleCompletionAudioEnded}
                preload="auto"
              />
            )}
            {completionHtml ? (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div
                ref={completionRef}
                className="ls-lesson-completion"
                onClick={handleCompletionClick}
                dangerouslySetInnerHTML={{ __html: completionHtml }}
              />
            ) : showCompletionLoader ? (
              // Waiting on the `feedback` frame — same loader as the story.
              <div className="story-preview-loader">
                <img
                  className="story-preview-loader-gif"
                  src={lessonGIFtransparent}
                  alt=""
                />
              </div>
            ) : (
              // Fallback when the real screen can't arrive (socket error /
              // timeout), or the submit itself failed and needs a retry.
              endSlot
            )}
          </div>
        </div>
      ) : (
        <LowerSchoolStoryStage
          status={status}
          intro={intro}
          node={node}
          socketError={socketError}
          startStory={startStory}
          answerQuestion={answerQuestion}
          goBack={goBack}
          sessionKey={roomId}
          endSlot={endSlot}
          frameless
        //   loaderCaption={LOADER_CAPTION}
          loaderNote={loaderNote}
          autoPlayNarration={autoPlayNarration}
          headerAction={fullscreenButton}
          isFullscreen={isPageFullscreen}
        />
      )}
    </div>
    {/* <FloatingAIButton
          onClick={() => {
            const agentData = [
              {
                key: course?.name || "Course",
                value: String(course?.course_id || ""),
              },
            ];

            dispatch(setAgentData(agentData));
            if (course?.course_id) {
              dispatch(setInitialProjectId(String(course?.course_id)));
            }
            dispatch(setDrawerState(!chatDrawerState.isOpen));
            dispatch(setIsWebAllowed(true));
            dispatch(setQueryMetaData(null));
          }}
        /> */}
    </>
  );
};

export default LSLesson;
